// src/components/layout-ui/SideBar.jsx
import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  Popover,
} from "@mui/material";
import {
  MenuOpen,
  Menu as MenuIcon,
  SettingsBrightness,
  Logout,
  Print,
  Dashboard,
  SettingsSuggest,
  Group,
  FitScreen,
  History,
  Receipt,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { logout } from "../../store/authSlice";
import ThemeToggle from "../buttons/ThemeToggle";
import FullscreenToggle from "../buttons/FullscreenToggle";
import LanguageSelector from "./LanguageSelector";
import { blueGrey, yellow } from "@mui/material/colors";
import { startTransition } from "react";
import logo from "../../assets/images/newlogo-primary.png";

const SIDEBAR_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const SideBar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const themeMenuOpen = Boolean(themeMenuAnchor);

  const topNavLinks = [
    { key: "dashboard", icon: <Dashboard />, link: "/" },
    { key: "printRequests", icon: <Print />, link: "/requests" },
    { key: "history", icon: <History />, link: "/history" },
    { key: "services", icon: <SettingsSuggest />, link: "/services" },
    { key: "configuration", icon: <Group />, link: "/configuration" },
    { key: "transactions", icon: <Receipt />, link: "/transactions" },

  ];

  const bottomNavLinks = [
    {
      name: <FullscreenToggle />,
      icon: <FitScreen />,
      isFullscreen: true, // Special marker for fullscreen
    },
    {
      name: (
        <Box display="flex" gap={2} alignItems="center">
          <span>{t("preference")}</span>
          <ThemeToggle />
        </Box>
      ),
      icon: <SettingsBrightness />,
      isTheme: true, // Special marker for theme
    },
    { name: t("logout"), icon: <Logout />, action: "logout" },
  ];

  const handleLogoutDialog = {
    open: () => setLogoutDialogOpen(true),
    confirm: () => {
      setLogoutDialogOpen(false);
      dispatch(logout());
      startTransition(() => {
        navigate("/login", { replace: true });
      });
    },
    cancel: () => setLogoutDialogOpen(false),
  };

  const renderLinkItem = (link, index) => {
    const isActive = link.link === location.pathname;
    const handleClick = () => {
      if (link.action === "logout") {
        handleLogoutDialog.open();
      } else if (link.link) {
        navigate(link.link);
      }
    };

    // Special handling for fullscreen
    if (link.isFullscreen) {
      return (
        <ListItem key={index} disablePadding sx={{ mb: 0.25 }}> {/* ✅ Reduced from mb: 0 to 0.25 for consistency */}
          <Tooltip title={collapsed ? t("enterFullScreen") : ""} placement="right" arrow>
            <Box sx={{ width: "100%" }}>
              <FullscreenToggle collapsed={collapsed} />
            </Box>
          </Tooltip>
        </ListItem>
      );
    }

    // Special handling for theme when collapsed
    if (link.isTheme && collapsed) {
      return (
        <React.Fragment key={index}>
          <ListItem disablePadding sx={{ mb: 0.25 }}>
            <Tooltip title={t("preference")} placement="right" arrow>
              <ListItemButton
                onClick={(e) => setThemeMenuAnchor(e.currentTarget)}
                sx={{
                  minHeight: 44,
                  px: 1.5,
                  borderRadius: 2,
                  justifyContent: "center",
                  "&:hover": { bgcolor: theme.palette.action.hover },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: yellow[700],
                    minWidth: 0,
                    justifyContent: "center",
                  }}
                >
                  {link.icon}
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <Popover
            open={themeMenuOpen}
            anchorEl={themeMenuAnchor}
            onClose={() => setThemeMenuAnchor(null)}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            slotProps={{
              paper: {
                sx: {
                  ml: 1,
                  bgcolor: "transparent",
                  boxShadow: "none",
                },
              },
            }}
          >
            <ThemeToggle />
          </Popover>
        </React.Fragment>
      );
    }

    return (
      <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
        <Tooltip title={collapsed ? t(link.key) : ""} placement="right" arrow>
          <ListItemButton
            selected={isActive}
            onClick={handleClick}
            sx={{
              minHeight: 48,
              px: collapsed ? 1.5 : 3,
              py: 0.75, 
              borderRadius: 2,
              bgcolor: isActive ? theme.palette.action.selected : "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover },
              justifyContent: collapsed ? "center" : "flex-start",
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? yellow[500] : yellow[700],
                minWidth: 0,
                mr: collapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              {link.icon}
            </ListItemIcon>

            {!collapsed && (
              <ListItemText
                primary={link.key ? t(link.key) : link.name}
                primaryTypographyProps={{
                  fontWeight: isActive ? 500 : 400,
                  fontSize: 15, 
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          overflow: "auto",
          borderRadius: 0,
          borderRight: `1px ${theme.palette.divider} solid`,
          transition: "width 0.2s",
          bgcolor: blueGrey[900],
          color: "white",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack py={2} spacing={2} sx={{ flexGrow: 1 }}>
          {/* Logo and Collapse Button */}
          <Box
            display="flex"
            alignItems="center"
            px={collapsed ? 1 : 2}
            justifyContent={collapsed ? "center" : "space-between"}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {!collapsed && (
                <img src={logo} alt="brand" style={{ width: 42, height: 42 }} />
              )}
              {!collapsed && (
                <Typography variant="h6" fontWeight={700}>
                  Zerox Cloud
                </Typography>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={() => setCollapsed((p) => !p)}
              sx={{
                ml: collapsed ? 0 : 1,
                color: yellow[400],
                bgcolor: theme.palette.action.selected,
                "&:hover": { bgcolor: theme.palette.action.hover },
              }}
            >
              {collapsed ? <MenuIcon /> : <MenuOpen />}
            </IconButton>
          </Box>

          <Divider sx={{ bgcolor: theme.palette.divider, mx: collapsed ? 1 : 2 }} />

          {/* Top navigation + Language */}
          <List sx={{ px: 1 }}>
            {topNavLinks.map(renderLinkItem)}
            <LanguageSelector collapsed={collapsed} />
          </List>
        </Stack>

        {/* Bottom navigation */}
        <List sx={{ px: 1, mb: 2 }}>
          {bottomNavLinks.map(renderLinkItem)}
        </List>
      </Paper>

      {/* Logout Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutDialog.cancel}>
        <DialogTitle>{t("logoutTitle")}</DialogTitle>
        <DialogContent sx={{ minWidth: { md: "540px" } }}>
          <DialogContentText>{t("logoutMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ mb: 2, px: 2 }}>
          <Button onClick={handleLogoutDialog.cancel}>{t("cancel")}</Button>
          <Button onClick={handleLogoutDialog.confirm} variant="contained" autoFocus>
            {t("logout")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SideBar;
